<script setup>
// 格式信息页（type-2 纯 SSR，nuxt#8）。逐字复刻 design 的 *Format.html 结构;数据来自 getFormatSEO。
import { ref } from "vue";

defineProps({ seo: { type: Object, required: true } });

const openFaq = ref(-1);

const IC_USE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const OP_IC = {
  convert: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h13l-3-3M20 16H7l3 3"/></svg>`,
  compress: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 9V4h-5M3 15v5h5M21 4l-7 7M3 20l7-7"/></svg>`,
  edit: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z"/></svg>`,
};
function rows(seo) {
  const n = Math.max(seo.pros.length, seo.cons.length);
  return Array.from({ length: n }, (_, i) => [seo.pros[i] || "", seo.cons[i] || ""]);
}
</script>

<template>
  <div class="ft-scope" :style="{ '--cc': seo.cc }">
    <!-- 面包屑 -->
    <div class="wrap">
      <div class="crumb">
        <template v-for="(c, i) in seo.crumb" :key="i">
          <NuxtLink v-if="i < seo.crumb.length - 1" :to="c[1]">{{ c[0] }}</NuxtLink>
          <span v-else class="cur">{{ c[0] }}</span>
          <span v-if="i < seo.crumb.length - 1" class="sep">/</span>
        </template>
      </div>
    </div>

    <!-- Hero -->
    <section class="fd-hero">
      <div class="wrap in">
        <div class="fd-badge">{{ seo.badge }}</div>
        <h1>What is a <span class="hl">.{{ seo.h1Ext }}</span> file?</h1>
        <p>{{ seo.intro }}</p>
        <div class="fd-facts">
          <div v-for="(f, i) in seo.facts" :key="i" class="fd-fact">
            <div class="k">{{ f[0] }}</div>
            <div class="v">{{ f[1] }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- How to open -->
    <section class="block tight">
      <div class="wrap">
        <div class="sec-h"><h2>{{ seo.howOpenTitle }}</h2></div>
        <div class="prose"><p>{{ seo.open }}</p></div>
      </div>
    </section>

    <!-- Common uses -->
    <section class="block tight">
      <div class="wrap">
        <div class="sec-h"><h2>{{ seo.usesTitle }}</h2></div>
        <div class="why-grid">
          <div v-for="(u, i) in seo.uses" :key="i" class="why-card">
            <div class="wi" :style="{ background: 'color-mix(in oklab,' + seo.cc + ' 15%,transparent)', color: seo.cc }" v-html="IC_USE"></div>
            <h4>{{ u[0] }}</h4>
            <p>{{ u[1] }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Strengths & limitations -->
    <section class="block tight">
      <div class="wrap">
        <div class="sec-h"><h2>Strengths &amp; limitations</h2></div>
        <div class="cmp-table" style="max-width: 760px">
          <table>
            <thead><tr><th style="width: 50%">✔ Strengths</th><th>✖ Limitations</th></tr></thead>
            <tbody>
              <tr v-for="(r, i) in rows(seo)" :key="i">
                <td style="color: #7ee6c0">{{ r[0] }}</td>
                <td style="color: #fb7185">{{ r[1] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Tools for this format -->
    <section v-if="seo.tools.length" class="block tight">
      <div class="wrap">
        <div class="sec-h"><h2>Tools for this format</h2><p>Convert, compress and edit — free, in your browser.</p></div>
        <div class="fd-tools">
          <NuxtLink v-for="(t, i) in seo.tools" :key="i" class="fd-tool" :to="t.href">
            <span class="ti" v-html="OP_IC[t.op] || OP_IC.convert"></span>
            <span><span class="tn">{{ t.name }}</span><span v-if="t.badge" class="tb">{{ t.badge }}</span></span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- AI 引流 -->
    <section class="block tight">
      <div class="wrap">
        <div class="upsell" style="background: linear-gradient(180deg, var(--surface), var(--bg-2))">
          <div class="upsell-in">
            <h2>Need more than a conversion?</h2>
            <p>{{ seo.aiSub }}</p>
            <div style="display: flex; gap: 12px; justify-content: center; flex-wrap: wrap">
              <NuxtLink class="btn btn-primary btn-lg" to="/">Try AI tools</NuxtLink>
              <NuxtLink class="btn btn-outline btn-lg" to="/ai/effects/">AI Effects</NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="block tight">
      <div class="wrap">
        <div class="sec-h"><h2>Frequently asked questions</h2></div>
        <div class="faq-list">
          <div v-for="(f, i) in seo.faqs" :key="i" class="faq-item" :class="{ open: openFaq === i }">
            <button class="faq-q" type="button" @click="openFaq = openFaq === i ? -1 : i">
              {{ f[0] }}
              <svg class="fic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14" stroke-linecap="round" /></svg>
            </button>
            <div class="faq-a"><div class="faq-a-in"><p>{{ f[1] }}</p></div></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Related formats -->
    <section class="block tight">
      <div class="wrap">
        <div class="sec-h"><h2>Related formats</h2></div>
        <div class="fd-cloud">
          <NuxtLink v-for="(r, i) in seo.related" :key="i" :to="'/format/' + r.slug + '/'">.{{ r.ext }}</NuxtLink>
          <span v-if="!seo.related.length" style="color: var(--tx-faint)">—</span>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.fd-hero { padding: 36px 0 26px; text-align: center; position: relative; overflow: hidden; }
.fd-hero::before { content: ""; position: absolute; inset: 0; background: radial-gradient(70% 90% at 50% -10%, color-mix(in oklab, var(--cc) 18%, transparent), transparent 60%); pointer-events: none; }
.fd-hero .in { position: relative; z-index: 1; }
.fd-badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 14px; border: 1px solid color-mix(in oklab, var(--cc) 40%, var(--line-strong)); border-radius: 999px; background: color-mix(in oklab, var(--cc) 12%, transparent); font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--cc); margin-bottom: 18px; }
.fd-hero h1 { font-size: clamp(34px, 6vw, 60px); font-weight: 700; }
.fd-hero h1 :deep(.hl), .fd-hero h1 .hl { color: var(--cc); }
.fd-hero p { font-size: clamp(16px, 2vw, 19px); color: var(--tx-dim); max-width: 640px; margin: 16px auto 0; line-height: 1.6; }
.fd-facts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; max-width: 760px; margin: 30px auto 0; }
.fd-fact { border: 1px solid var(--line); border-radius: 13px; background: var(--surface); padding: 15px 16px; text-align: left; }
.fd-fact .k { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--tx-faint); }
.fd-fact .v { font-family: var(--font-h); font-weight: 600; font-size: 15px; margin-top: 4px; color: var(--tx); word-break: break-word; }
.fd-tools { display: grid; grid-template-columns: repeat(3, 1fr); gap: 13px; }
.fd-tool { display: flex; align-items: center; gap: 12px; padding: 15px 16px; border: 1px solid var(--line); border-radius: 13px; background: var(--surface); transition: 0.16s; }
.fd-tool:hover { border-color: var(--cc); background: var(--surface-2); transform: translateY(-2px); }
.fd-tool .ti { width: 38px; height: 38px; border-radius: 10px; flex: none; display: grid; place-items: center; background: color-mix(in oklab, var(--cc) 16%, transparent); color: var(--cc); }
.fd-tool .ti :deep(svg) { width: 19px; height: 19px; }
.fd-tool .tn { display: block; font-family: var(--font-h); font-weight: 600; font-size: 14.5px; line-height: 1.2; }
.fd-tool .tb { display: block; font-size: 12px; color: var(--tx-faint); font-weight: 600; margin-top: 2px; }
.fd-cloud { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; }
.fd-cloud a { font-size: 13.5px; font-weight: 600; color: var(--tx-dim); border: 1px solid var(--line); border-radius: 99px; padding: 8px 15px; transition: 0.15s; }
.fd-cloud a:hover { color: var(--tx); border-color: var(--cc); background: color-mix(in oklab, var(--cc) 10%, transparent); }
@media (max-width: 760px) { .fd-tools { grid-template-columns: 1fr; } .fd-facts { grid-template-columns: 1fr 1fr; } }
</style>
